import { CREATOR_API_PREFIX } from '@ftedn/shared';
import { getAppCheckToken } from '../services/app-check';

// FTEDN — API client hook
let cachedBase: string | null = null;

interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
  };
}

function getConfiguredBase(): string {
  if (cachedBase) return cachedBase;

  const prefix = import.meta.env.VITE_API_PREFIX || CREATOR_API_PREFIX;
  const origin = (import.meta.env.VITE_API_ORIGIN || import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

  cachedBase = origin ? `${origin}${prefix}` : prefix;
  return cachedBase;
}

class ApiClient {
  private async getHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const storage = typeof globalThis.localStorage === 'undefined' ? null : globalThis.localStorage;
    const token = storage && typeof storage.getItem === 'function' ? storage.getItem('ftedn_token') : null;
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const appCheckToken = await getAppCheckToken();
    if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;
    return headers;
  }

  private async getBase(): Promise<string> {
    return getConfiguredBase();
  }

  private async buildRequestError(res: Response, method: string, path: string): Promise<Error> {
    try {
      const payload = await res.clone().json() as ApiErrorPayload;
      if (payload.error?.message) {
        return new Error(payload.error.message);
      }
    } catch {
      // Fall back to status-based errors when the API returns an empty/non-JSON body.
    }

    return new Error(`${method} ${path} failed: ${res.status}`);
  }

  async get(path: string) {
    const base = await this.getBase();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch(`${base}${path}`, { headers: await this.getHeaders(), signal: controller.signal });
      if (!res.ok) throw await this.buildRequestError(res, 'GET', path);
      return res.json();
    } finally { clearTimeout(timeout); }
  }

  async post(path: string, body?: unknown) {
    const base = await this.getBase();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch(`${base}${path}`, {
        method: 'POST', headers: await this.getHeaders(), body: JSON.stringify(body), signal: controller.signal,
      });
      if (!res.ok) throw await this.buildRequestError(res, 'POST', path);
      return res.json();
    } finally { clearTimeout(timeout); }
  }

  async patch(path: string, body?: unknown) {
    const base = await this.getBase();
    const res = await fetch(`${base}${path}`, {
      method: 'PATCH', headers: await this.getHeaders(), body: JSON.stringify(body),
    });
    if (!res.ok) throw await this.buildRequestError(res, 'PATCH', path);
    return res.json();
  }

  async delete(path: string) {
    const base = await this.getBase();
    const res = await fetch(`${base}${path}`, {
      method: 'DELETE', headers: await this.getHeaders(),
    });
    if (!res.ok) throw await this.buildRequestError(res, 'DELETE', path);
    return res.json();
  }
}

export const apiClient = new ApiClient();
